import {PurchaseLimit, PurchaseLimitUnit, SubscriptionActivity} from '../api/subscriptions/types'
import {OpeningTimes} from '../api/types'

export type DayKey = keyof OpeningTimes

// Monday first: the app's week and the server's key order.
export const DAY_KEYS: DayKey[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

// Existing short day names in the translation files ("Mon", "Пн", ...).
export const DAY_LABEL_KEYS = {
  monday: 'mon',
  tuesday: 'tue',
  wednesday: 'wed',
  thursday: 'thu',
  friday: 'fri',
  saturday: 'sat',
  sunday: 'sun',
} as const

export type FormatOpeningTimesOptions = {
  dayLabels: Record<DayKey, string>
  // Shown for a 00:00-23:59 window instead of "00:00–23:59".
  allDayLabel?: string
  // Keep each "09:00–17:00" left-to-right when the line sits inside right-to-left text (Hebrew).
  isolateTimes?: boolean
}

const RANGE_SEPARATOR = '–'
const GROUP_SEPARATOR = ' · '
const LRI = '⁦'
const PDI = '⁩'

const normaliseTime = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null
  }
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim())
  return match ? `${match[1].padStart(2, '0')}:${match[2]}` : null
}

type DayWindow = {from: string; to: string}

const getDayWindow = (openingTimes: Partial<OpeningTimes>, day: DayKey): DayWindow | null => {
  const schedule = openingTimes[day]
  if (!schedule || !schedule.isActive) {
    return null
  }
  const from = normaliseTime(schedule.from)
  const to = normaliseTime(schedule.to)
  return from && to ? {from, to} : null
}

/**
 * "Mon–Fri 09:00–17:00 · Sat 10:00–14:00": consecutive days with the same hours are merged.
 * A window whose end is before its start runs past midnight and is shown as given ("22:00–02:00").
 * Returns '' when no day has valid hours.
 */
export const formatOpeningTimes = (
  openingTimes: Partial<OpeningTimes> | null | undefined,
  {dayLabels, allDayLabel, isolateTimes}: FormatOpeningTimesOptions,
): string => {
  if (!openingTimes) {
    return ''
  }
  const groups: {first: DayKey; last: DayKey; window: DayWindow; lastIndex: number}[] = []
  DAY_KEYS.forEach((day, index) => {
    const window = getDayWindow(openingTimes, day)
    if (!window) {
      return
    }
    const group = groups[groups.length - 1] as (typeof groups)[number] | undefined
    if (group && group.lastIndex === index - 1 && group.window.from === window.from && group.window.to === window.to) {
      group.last = day
      group.lastIndex = index
      return
    }
    groups.push({first: day, last: day, window, lastIndex: index})
  })

  return groups
    .map(({first, last, window}) => {
      const days = first === last ? dayLabels[first] : `${dayLabels[first]}${RANGE_SEPARATOR}${dayLabels[last]}`
      const isAllDay = window.from === '00:00' && (window.to === '23:59' || window.to === '24:00')
      const hours =
        isAllDay && allDayLabel ? allDayLabel : `${window.from}${RANGE_SEPARATOR}${window.to}`
      return `${days} ${isolateTimes ? `${LRI}${hours}${PDI}` : hours}`
    })
    .join(GROUP_SEPARATOR)
}

export type PassHoursGroup = {
  text: string
  activities: SubscriptionActivity[]
}

/**
 * The hours a pass can be used, from its activities. Only activities with `allTheTime === false` count.
 * Activities that share the same hours are returned together, so a pass whose activities all have the
 * same hours gives exactly one group.
 */
export const getPassHoursGroups = (
  activities: Pick<SubscriptionActivity, 'allTheTime' | 'entities' | 'openingTimes'>[] | null | undefined,
  options: FormatOpeningTimesOptions,
): PassHoursGroup[] => {
  const groups: PassHoursGroup[] = []
  ;(activities || []).forEach((activity) => {
    if (activity.allTheTime !== false) {
      return
    }
    const text = formatOpeningTimes(activity.openingTimes, options)
    if (!text) {
      return
    }
    const existing = groups.find((group) => group.text === text)
    if (existing) {
      existing.activities.push(activity as SubscriptionActivity)
      return
    }
    groups.push({text, activities: [activity as SubscriptionActivity]})
  })
  return groups
}

export type PurchaseLimitKey =
  | 'pass_limit_once'
  | 'pass_limit_per_day'
  | 'pass_limit_per_month'
  | 'pass_limit_per_week'
  | 'pass_limit_per_year'

const PERIOD_LIMIT_KEYS: Record<PurchaseLimitUnit, PurchaseLimitKey> = {
  DAY: 'pass_limit_per_day',
  WEEK: 'pass_limit_per_week',
  MONTH: 'pass_limit_per_month',
  YEAR: 'pass_limit_per_year',
}

/** "Once per person, ever" / "1 per week". '' when there is no (valid) limit. */
export const formatPurchaseLimit = (
  limit: PurchaseLimit | null | undefined,
  translate: (key: PurchaseLimitKey, values?: {max: number}) => string,
): string => {
  if (!limit) {
    return ''
  }
  if (limit.type === 'ONCE') {
    return translate('pass_limit_once')
  }
  const key = PERIOD_LIMIT_KEYS[limit.unit] as PurchaseLimitKey | undefined
  return limit.max > 0 && key ? translate(key, {max: limit.max}) : ''
}
